import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { QueryContextFromKeys, QueryParamModel } from 'models';
import {
  CreatePermissionGroupPayload,
  UpdatePermissionGroupMembersPayload,
  UpdatePermissionGroupPayload,
  permissionGroupService,
} from 'services/permissionGroup.service';
import { permissionScopeService } from 'services/permissionScope.service';
import { permissionNotificationService } from 'services/permissionNotification.service';

type PermissionGroupQueryContexts = QueryContextFromKeys<typeof permissionGroupKeys>;
type PermissionScopeQueryContexts = QueryContextFromKeys<typeof permissionScopeKeys>;
type PermissionNotificationQueryContexts = QueryContextFromKeys<typeof permissionNotificationKeys>;

export const permissionGroupKeys = {
  all: [{ scope: 'permissionGroups' }] as const,
  lists: () => [{ ...permissionGroupKeys.all[0], entity: 'list' }] as const,
  list: (params?: QueryParamModel | null) => [{ ...permissionGroupKeys.lists()[0], params }] as const,
  searchList: () => [{ ...permissionGroupKeys.all[0], entity: 'search' }] as const,
  details: () => [{ ...permissionGroupKeys.all[0], entity: 'detail' }] as const,
  detail: (id: ID) => [{ ...permissionGroupKeys.details()[0], id }] as const,
};

export const permissionScopeKeys = {
  all: [{ scope: 'permissionScopes' }] as const,
  catalogue: () => [{ ...permissionScopeKeys.all[0], entity: 'catalogue' }] as const,
};

export const permissionNotificationKeys = {
  all: [{ scope: 'permissionNotifications' }] as const,
  catalogue: () => [{ ...permissionNotificationKeys.all[0], entity: 'catalogue' }] as const,
};

const fetchPermissionGroupList = async ({
  queryKey: [{ params }],
}: PermissionGroupQueryContexts['list']) => permissionGroupService.getPermissionGroupList(params);

const fetchPermissionGroupListSearch = async () => permissionGroupService.getPermissionGroupListSearch();

const fetchPermissionGroup = async ({ queryKey: [{ id }] }: PermissionGroupQueryContexts['detail']) =>
  permissionGroupService.getPermissionGroup(id);

const fetchScopeCatalogue = async (_: PermissionScopeQueryContexts['catalogue']) =>
  permissionScopeService.getScopeCatalogue();

const fetchNotificationCatalogue = async (_: PermissionNotificationQueryContexts['catalogue']) =>
  permissionNotificationService.getNotificationCatalogue();

export const useScopeCatalogue = () => {
  return useQuery({
    queryKey: permissionScopeKeys.catalogue(),
    queryFn: fetchScopeCatalogue,
    staleTime: Infinity,
  });
};

export const useNotificationCatalogue = () => {
  return useQuery({
    queryKey: permissionNotificationKeys.catalogue(),
    queryFn: fetchNotificationCatalogue,
    staleTime: Infinity,
  });
};

export const usePermissionGroupList = (params?: QueryParamModel | null) => {
  return useQuery({ queryKey: permissionGroupKeys.list(params), queryFn: fetchPermissionGroupList });
};

// Unpaginated, for in-memory pickers (e.g. the OPEM users multiselect) - mirrors useUserListSearch.
export const usePermissionGroupListSearch = () => {
  return useQuery({ queryKey: permissionGroupKeys.searchList(), queryFn: fetchPermissionGroupListSearch });
};

export const usePermissionGroup = (id: ID) => {
  return useQuery({ queryKey: permissionGroupKeys.detail(id), queryFn: fetchPermissionGroup });
};

export const useCreatePermissionGroup = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreatePermissionGroupPayload) => permissionGroupService.createPermissionGroup(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: permissionGroupKeys.all });
    },
  });
};

export const useUpdatePermissionGroup = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: ID; data: UpdatePermissionGroupPayload }) =>
      permissionGroupService.updatePermissionGroup(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: permissionGroupKeys.all });
      queryClient.invalidateQueries({ queryKey: permissionGroupKeys.detail(id) });
    },
  });
};

export const useUpdatePermissionGroupMembers = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: ID; data: UpdatePermissionGroupMembersPayload }) =>
      permissionGroupService.updatePermissionGroupMembers(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: permissionGroupKeys.lists() });
      queryClient.invalidateQueries({ queryKey: permissionGroupKeys.detail(id) });
    },
  });
};
